import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { NutritionPreset, CreateNutritionPreset, UpdateNutritionPreset } from '../types'

export function useNutritionPresets(userId: string) {
  return useQuery({
    queryKey: ['nutrition_presets', userId],
    queryFn: async (): Promise<NutritionPreset[]> => {
      const { data, error } = await supabase
        .from('nutrition_presets')
        .select('*')
        .eq('user_id', userId)
        .order('name')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!userId
  })
}

export function useCreateNutritionPreset() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newPreset: CreateNutritionPreset): Promise<NutritionPreset> => {
      const { data, error } = await supabase
        .from('nutrition_presets')
        .insert(newPreset)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nutrition_presets', data.user_id] })
    }
  })
}

export function useUpdateNutritionPreset() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateNutritionPreset }): Promise<NutritionPreset> => {
      const { data, error } = await supabase
        .from('nutrition_presets')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nutrition_presets', data.user_id] })
    }
  })
}

export function useDeleteNutritionPreset() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<NutritionPreset> => {
      // Get the preset first to know which user to invalidate
      const { data: preset, error: selectError } = await supabase
        .from('nutrition_presets')
        .select('*')
        .eq('id', id)
        .single()
      
      if (selectError) throw handleSupabaseError(selectError)
      if (!preset) throw new Error('Preset not found')
      
      const { error: deleteError } = await supabase
        .from('nutrition_presets')
        .delete()
        .eq('id', id)
      
      if (deleteError) throw handleSupabaseError(deleteError)
      return preset
    },
    onSuccess: (preset) => {
      queryClient.invalidateQueries({ queryKey: ['nutrition_presets', preset.user_id] })
    }
  })
}